import React from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import { DateTime } from 'luxon'
import * as AppDefinitions from '../../../App.d'
import { Messages, useMessageReducer } from '../../ui/messages/messages'
import * as Request from '../../../utilities/request'
import { PricingCard } from '../../ui/pricingCard/PricingCard'
import { OrgSettings } from '../../../screens/subsync/subsync'

interface BillingComponentFrameProps {
	show: boolean
	appState: AppDefinitions.AppState
	orgSettings: OrgSettings
}

export const BillingComponent = (props: BillingComponentFrameProps) => {
	const [messages, updateMessages] = useMessageReducer([])
	const [portalUrl, setPortalUrl] = React.useState<string | null>(null)

	React.useEffect(() => {
		const fetchPortalUrl = async () => {
			const req = await Request.get('subscription/portal', props.appState.authState)
			if (req.data && req.data.portal.url) {
				setPortalUrl(req.data.portal.url)
			}
		}

		if (props.appState.authState.isLoggedIn) {
			fetchPortalUrl()
		}
	}, [props.appState.authState.isLoggedIn, props.show])

	if (!props.show) {
		return null
	}

	const planLabel =
		(props.appState.subscription?.currentSubscriptionName ? 'SubSync' : 'No active subscription') + (props.appState.subscription?.isTrial ? ' (Trial)' : '')

	const handleCheckoutError = (message: string) => {
		updateMessages({
			type: 'add',
			data: { severity: 'danger', message, dismissible: true, timeout: 5000 },
		})
	}

	return (
		<>
			<Card className="subsync-card mb-2">
				<Card.Header>
					<Row>
						<Col style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '15px' }}>
							<h3>Billing</h3>
						</Col>
					</Row>
				</Card.Header>
				<Card.Body>
					<Row className="subsync-card-inner-row">
						<Col>
							{props.appState.subscription !== null && (
								<>
									{props.appState.subscription.cancelledAt === null ||
									(props.appState.subscription.cancelledAt &&
										DateTime.fromISO(props.appState.subscription.cancelledAt).diffNow('seconds').seconds > 0) ? (
										<>
											<p>
												Current subscription: <strong>{planLabel}</strong> (<strong>{props.appState.subscription.usage}</strong>{' '}
												invoices processed during current billing period{' '}
												{props.appState.subscription.currentPeriodStart && props.appState.subscription.currentPeriodEnd && (
													<strong>
														{DateTime.fromISO(props.appState.subscription.currentPeriodStart).toLocaleString(DateTime.DATE_MED)} -{' '}
														{DateTime.fromISO(props.appState.subscription.currentPeriodEnd).toLocaleString(DateTime.DATE_MED)}
													</strong>
												)}
												)
											</p>
											<p>
												Current incurred costs <strong>${props.appState.subscription.currentCost?.toFixed(2)}</strong>{' '}
												{props.appState.subscription.isTrial ? '(after your trial)' : ''}, projected approximate end of billing period
												cost <strong>${props.appState.subscription.projectedCost?.toFixed(2)}</strong> (
												<strong>{props.appState.subscription.projectedUsage?.toFixed(0)}</strong> invoices)
											</p>
											{portalUrl && (
												<p>
													Manage payment methods and view billing history in the <a href={portalUrl}>billing portal</a>.
												</p>
											)}
										</>
									) : (
										<>
											<p>You do not have a valid subscription</p>
											<PricingCard
												title="SubSync"
												description="Usage-based pricing"
												product="subsync"
												usageLabel="invoices"
												orgCurrency={props.orgSettings.xeroOrgs.at(0)?.xeroOrg?.baseCurrency?.toLowerCase() || 'aud'}
												minValue={1}
												maxValue={500}
												defaultValue={50}
												authState={props.appState.authState}
												onCheckoutError={handleCheckoutError}
											/>
										</>
									)}
								</>
							)}
							{props.appState.subscription === null && <p>Contact customer support to switch over to our new billing.</p>}
						</Col>
					</Row>
				</Card.Body>
			</Card>
			<Messages messages={messages} updateMessage={updateMessages} />
		</>
	)
}
